import { get_token, get_user_details } from "@/router";
import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue";
import StickyNav from "@/components/BottomNavbar/StickyNavbar.vue";
import InactivityModal from "@/components/InactivityTracker/InactivityTracker.vue";
import { homeFilterMethods } from "@/views/organisation/organisation_helpers";
import {
  get_organisation_section_18a_data,
  organisationSection18AMethods,
} from "@/views/organisation/organisation_section_18a_helper";

function normalize_section_18a_value(value) {
  return String(value || "").trim();
}

function get_empty_section_18a_form_errors() {
  return {
    registration_number: "",
    income_tax_reference_number: "",
    pbo_number: "",
    effective_date: "",
    approval_letter: "",
  };
}

function is_pdf_file(file) {
  if (!(file instanceof File)) {
    return true;
  }

  const file_name = String(file.name || "").toLowerCase();
  return file.type === "application/pdf" || file_name.endsWith(".pdf");
}

function get_section_18a_approval_letter_error(file) {
  if (!(file instanceof File)) {
    return "";
  }

  if (!is_pdf_file(file)) {
    return "Approval letter must be a PDF file.";
  }

  return "";
}

function normalize_section_18a_form_values(form = {}) {
  return {
    ...form,
    registration_number: normalize_section_18a_value(form.registration_number),
    income_tax_reference_number: normalize_section_18a_value(form.income_tax_reference_number),
    pbo_number: normalize_section_18a_value(form.pbo_number),
    effective_date: normalize_section_18a_value(form.effective_date),
  };
}

function get_registration_number_error(value) {
  if (!value) {
    return "Registration number is required.";
  }

  if (!/^\d{4}\/\d{6}\/\d{2}$/.test(value)) {
    return "Use the format CCYY/NNNNNN/NN, for example 2024/123456/08.";
  }

  return "";
}

function get_income_tax_reference_number_error(value) {
  if (!value) {
    return "Income tax reference number is required.";
  }

  if (!/^\d{10}$/.test(value)) {
    return "Income tax reference number must be exactly 10 digits.";
  }

  if (!/^[01239]/.test(value)) {
    return "Income tax reference number must start with 0, 1, 2, 3, or 9.";
  }

  return "";
}

function get_pbo_number_error(value) {
  if (!value) {
    return "PBO number is required.";
  }

  if (!/^\d{9}$/.test(value)) {
    return "PBO number must be exactly 9 digits.";
  }

  if (/^0{9}$/.test(value)) {
    return "PBO number cannot be all zeros.";
  }

  return "";
}

function get_effective_date_error(value) {
  if (!value) {
    return "Effective date is required.";
  }

  if (value > "2026-08-04") {
    return "Effective date cannot be after 2026-08-04.";
  }

  return "";
}

function get_section_18a_form_validation_errors(form = {}) {
  return {
    registration_number: get_registration_number_error(form.registration_number),
    income_tax_reference_number: get_income_tax_reference_number_error(form.income_tax_reference_number),
    pbo_number: get_pbo_number_error(form.pbo_number),
    effective_date: get_effective_date_error(form.effective_date),
    approval_letter: get_section_18a_approval_letter_error(form.approval_letter),
  };
}

export default {
  components: {
    HomeNavBar,
    StickyNav,
    InactivityModal,
  },
  data: () => ({
    token_exists: false,
    user_details: {},
    active_profile: null,
    saved_branding: null,
    company: {},
    show_section_18a_preview: false,
    ...get_organisation_section_18a_data(),
  }),
  computed: {
    organization_company_id() {
      return this.company?.id || this.user_details?.organization_id || null;
    },
    has_section_18a_config() {
      return !!this.section_18a_config?.id;
    },
    current_approval_letter() {
      return this.section_18a_config?.approval_letter?.[0] || null;
    },
    current_approval_letter_name() {
      return 'Approval letter';
    },
    current_approval_letter_url() {
      const file = this.current_approval_letter?.file;

      if (file && typeof file === 'object') {
        return file.url || file.file_url || '';
      }

      return this.current_approval_letter?.file_url || this.current_approval_letter?.url || '';
    },
    current_approval_letter_is_pdf() {
      return /\.pdf(\?|$)/i.test(this.current_approval_letter_url);
    },
    current_approval_letter_is_image() {
      return /\.(png|jpe?g|gif|webp)(\?|$)/i.test(this.current_approval_letter_url);
    },
    selected_approval_letter_name() {
      return this.section_18a_form?.approval_letter?.name || '';
    },
    is_npo_organization() {
      const normalized_type = String(this.company?.type_of_organization?.type_of_organization || '').trim().toLowerCase();
      return normalized_type === 'npo';
    },
    is_section_18a_dirty() {
      if (!this.has_section_18a_config) {
        return true;
      }

      const current_form = this.section_18a_form || {};
      const saved_form = this.section_18a_config || {};
      const fields = [
        'registration_number',
        'income_tax_reference_number',
        'pbo_number',
        'effective_date',
      ];

      return fields.some((field) => (
        normalize_section_18a_value(Reflect.get(current_form, field)) !== normalize_section_18a_value(Reflect.get(saved_form, field))
      )) || current_form.approval_letter instanceof File;
    },
  },
  mounted() {
    this.token_exists = !!get_token();
    this.user_details = get_user_details() || {};
    this.saved_branding = this.get_saved_branding();
    this.apply_branding_to_platform(this.saved_branding);

    this.$nextTick(() => {
      this.fetch_company_profile();
      this.fetch_section_18a_config();
    });

  },

  methods: {
    ...homeFilterMethods,
    ...organisationSection18AMethods,
    format_registration_number_input() {
      const digits_only = String(this.section_18a_form?.registration_number || '').replace(/\D/g, '').slice(0, 12);
      const first_part = digits_only.slice(0, 4);
      const second_part = digits_only.slice(4, 10);
      const third_part = digits_only.slice(10, 12);
      const formatted_parts = [first_part, second_part, third_part].filter(Boolean);

      this.section_18a_form = {
        ...this.section_18a_form,
        registration_number: formatted_parts.join('/'),
      };
    },
    validate_section_18a_form() {
      const normalized_form = normalize_section_18a_form_values(this.section_18a_form);
      const next_errors = get_section_18a_form_validation_errors(normalized_form);

      this.section_18a_form = normalized_form;
      this.section_18a_form_errors = {
        ...get_empty_section_18a_form_errors(),
        ...next_errors,
      };

      return !Object.values(next_errors).some(Boolean);
    },
    handle_section_18a_approval_letter_change(event) {
      const file = event?.target?.files?.[0] || null;
      this.section_18a_is_dragging = false;
      const file_error = get_section_18a_approval_letter_error(file);
      this.section_18a_form_errors = {
        ...this.section_18a_form_errors,
        approval_letter: file_error,
      };
      this.section_18a_form = {
        ...this.section_18a_form,
        approval_letter: !file_error ? file : null,
      };

      if (event?.target && file_error) {
        event.target.value = "";
      }
    },
    handle_section_18a_file_drop(event) {
      const file = event?.dataTransfer?.files?.[0] || null;
      this.section_18a_is_dragging = false;
      const file_error = get_section_18a_approval_letter_error(file);
      this.section_18a_form_errors = {
        ...this.section_18a_form_errors,
        approval_letter: file_error,
      };
      this.section_18a_form = {
        ...this.section_18a_form,
        approval_letter: !file_error ? file : null,
      };
    },
    clear_section_18a_approval_letter() {
      this.section_18a_is_dragging = false;
      this.section_18a_form_errors = {
        ...this.section_18a_form_errors,
        approval_letter: "",
      };
      this.section_18a_form = {
        ...this.section_18a_form,
        approval_letter: null,
      };
    },
  },
};
